<template>
  <div
    v-if="rowContentBlocks"
    class="container"
    :class="`bg--${rowBgColors[backgroundColor ?? RowBackgroundColours._0]}`"
    v-bind="useEpiAttrs(rowContentBlocks)">
    <ul class="grid__row">
      <li
        v-for="(item, index) in rowContentBlocks?.value?.content"
        :key="item.id ?? index"
        class="grid__block"
        :class="getClasses(item, index)">
        <BlocksRowContentBlock
          :content="item.content"
          :image-style="getImageStyleClasses(item.content.imageStyle)" />
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import {
  type ContentAreaItemResponseModel,
  ImageStyles,
  RowBackgroundColours,
  type RowBlockResponseModel,
  RowLayouts,
} from '@api';
import { rowBgColors } from '@base/utils/theme';
import { getVideoIdFromYoutubeString } from '@base/utils/getVideoId';
import type { RowContentBlock } from '@base/types/models';

const props = defineProps<RowBlockResponseModel>();

const getImageStyleClasses = (imageStyle: ImageStyles | undefined): string => {
  if (imageStyle === undefined) return '';

  switch (imageStyle) {
    case ImageStyles._0:
      return '';
    case ImageStyles._1:
      return 'grid-block-img-large';
    case ImageStyles._2:
      return 'dir-col grid-block-icon';
    case ImageStyles._3:
      return 'dir-col grid-block-img-large';
  }
};

const getClasses = (blockItem: ContentAreaItemResponseModel, index: number): string[] => {
  const classes = [];
  const imageStyle = (blockItem.content as RowContentBlock).imageStyle;

  if (
    (props.layout === RowLayouts._1 && index === 1) ||
    (props.layout === RowLayouts._2 && index === 0)
  ) {
    classes.push('col-9');
  }

  classes.push(getImageStyleClasses(imageStyle));

  const youtubeIdUrl = getVideoIdFromYoutubeString(
    (blockItem.content as RowContentBlock).link?.value?.url ?? ''
  );

  if (youtubeIdUrl) {
    classes.push('grid-block-video');
  }

  return classes;
};
</script>

<style lang="scss" scoped>
.grid {
  &__row {
    @include reset-list;
    grid-column: 1 / 13;

    .grid-block-img-large {
      @include media-breakpoint-up(xl) {
        .grid__block-content {
          :deep(figure) {
            flex: 0 0 66.7%;
          }

          :deep(.grid__block-copy) {
            flex: 0 1 33.3%;
          }
        }
      }
    }
  }

  &__block-content {
    display: flex;
    width: 100%;
  }

  &__block {
    display: flex;
    min-height: px(288);
    min-width: calc(33.3% - 8px);
    border-radius: px(12);
    margin-bottom: px(16);
    overflow: hidden;

    &.col-9 {
      flex: 0 0 calc(66.67% - 8px);
    }

    &.grid-block-video {
      background-color: var(--color-black);
      place-items: center center;
      justify-content: center;

      .grid__block-content {
        max-width: px(693);
      }
    }

    &.dir-col.grid-block-img-large {
      :deep(.grid__block-content) {
        flex-direction: column;
      }
    }
  }
}

@include media-breakpoint-up(lg) {
  .container {
    @include color-bg-variants;
  }

  .grid {
    &__row {
      display: flex;
      grid-column: 2 / 12;
    }

    &__block {
      height: px(392);
      margin: px(4);
      flex: 0 1 calc(66.67% - 8px);

      &.dir-col {
        :deep(.grid__block-copy) {
          flex: 1 0 50%;
          padding: px(16) px(24);
          max-height: px(176);
        }
      }
    }

    &__block-content {
      overflow: hidden;
    }
  }

  :deep(.grid__block-figure) {
    min-height: px(196);
  }
}

@include media-breakpoint-up(xl) {
  .grid {
    &__row {
      padding-block: px(4);
      grid-column: 3 / 11;
    }
  }
}
</style>
